import React, { useState, useEffect } from 'react'
import {
  Mountain,
  Waves,
  TrendingUp,
  Compass,
  Droplets,
  Info,
  ShoppingCart,
  Check,
  AlertCircle,
  MapPin,
  Wind,
  Triangle,
  Shield,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { productsService, calculateProductPrice, isAtMinPrice } from '@core/products'
import type { Product } from '@core/products'

// Local UI metadata (icons + whenToUse copy, keyed by product code). The DB
// owns name/description/price/category; the frontend owns icons + guidance.
// Ported from frontend ProductPanel verbatim so the two pickers stay in sync.

interface ProductMeta {
  icon: LucideIcon
  whenToUse: string
}

const PRODUCT_META: Record<string, ProductMeta> = {
  // Terreno Base
  'mdt-2m': {
    icon: Mountain,
    whenToUse: 'Base para todos os outros produtos. Essencial para análise topográfica, planeamento de drenagem e cálculo de volumes. Resolução 2m via LiDAR DGT.',
  },
  'hillshade': {
    icon: Mountain,
    whenToUse: 'Use como camada de visualização do relevo em preto e branco. Combina bem com qualquer outro produto para melhor leitura do terreno.',
  },
  'hillshade-hypsometric': {
    icon: Mountain,
    whenToUse: 'Versão colorida do sombreamento. Cor codifica altitude, brilho codifica sombras. Excelente para apresentações e relatórios.',
  },
  'contours-1m': {
    icon: Waves,
    whenToUse: 'Para projectos detalhados: construção, jardins, terraços, keyline design. Intervalo 1m de alta resolução (LiDAR 50cm). Gera muitas linhas em áreas grandes.',
  },
  // Morfologia
  'slope': {
    icon: TrendingUp,
    whenToUse: 'Para identificar áreas de risco (>30°), planear acessos e estradas, ou avaliar aptidão agrícola por classe de declive.',
  },
  'aspect': {
    icon: Compass,
    whenToUse: 'Para planeamento de vinhas, painéis solares, ou selecção de culturas com base na orientação das encostas (N, S, E, W).',
  },
  'geomorphons': {
    icon: Mountain,
    whenToUse: 'Para identificar automaticamente 10 tipos de forma do terreno: cume, cumeada, nariz, encosta, rebordo, sela, planura, depressão, vale. Via WhiteboxTools.',
  },
  'wind-exposition': {
    icon: Wind,
    whenToUse: 'Para planeamento de abrigo e cortinas de vento. Identifica zonas muito expostas vs. abrigadas com base no índice de exposição ao vento (SAGA).',
  },
  // Hidrologia
  'streams': {
    icon: Droplets,
    whenToUse: 'Para identificar a rede de drenagem natural por ordens de Strahler (1-7+). Essencial para keyline design, gestão de água e delimitação de bacias.',
  },
  'ridges': {
    icon: Droplets,
    whenToUse: 'Par simétrico das linhas de água: identifica festos e divisórias de bacias. Obrigatório em conjunto com as Linhas de Água para leitura completa do terreno.',
  },
  'flow-accumulation': {
    icon: Droplets,
    whenToUse: 'Para visualizar onde a água converge. Escala logarítmica mostra desde pequenos riachos até linhas de água principais. SAGA ta_hydrology.',
  },
  'basins': {
    icon: Droplets,
    whenToUse: 'Para delimitar bacias hidrográficas e sub-bacias etiquetadas por área (A1, A2, B1...). Útil para análise de drenagem e planeamento hídrico.',
  },
  'flood-zones': {
    icon: Droplets,
    whenToUse: 'Para identificar áreas com risco de inundação. 3 classes de risco (Elevado, Moderado, Baixo) pelo método GFI com calibração mediterrânica.',
  },
  // Erosão
  'erosion-risk': {
    icon: TrendingUp,
    whenToUse: 'Para identificar zonas vulneráveis à erosão usando o factor LS (RUSLE). Essencial antes de qualquer intervenção no solo ou planeamento de coberto vegetal.',
  },
  // Landforms & Keyline
  'keypoints': {
    icon: MapPin,
    whenToUse: 'Para encontrar os pontos de inflexão de vale — ponto de partida do Keyline Design (P.A. Yeomans 1954). Um keypoint por linha de água. Requer LiDAR 50cm.',
  },
  'primary-valleys': {
    icon: Triangle,
    whenToUse: 'Polígonos dos vales primários (geomorphon classe 9). Zonas naturais de acumulação de água e potenciais locais para charcas e biodiversidade.',
  },
  'primary-ridges': {
    icon: Triangle,
    whenToUse: 'Polígonos das cumeadas primárias (geomorphon classe 3). Par simétrico dos vales, útil para planeamento de acessos e cortinas de vento.',
  },
  // Institucional
  'restricoes-legais': {
    icon: Shield,
    whenToUse: 'Para verificar se o terreno está sob Reserva Ecológica Nacional (REN) ou Reserva Agrícola Nacional (RAN). Devolve o clip vetorial das áreas restringidas, percentagem de cobertura e referências aos diplomas legais. Essencial antes de construção ou mobilização de solo.',
  },
}

interface GroupMeta {
  label: string
  order: number
}

const GROUP_META: Record<string, GroupMeta> = {
  terreno_base:  { label: 'Terreno Base',        order: 1 },
  morfologia:    { label: 'Morfologia',          order: 2 },
  hidrologia:    { label: 'Hidrologia',          order: 3 },
  erosao:        { label: 'Erosão',              order: 4 },
  landforms:     { label: 'Landforms & Keyline', order: 5 },
  institucional: { label: 'Institucional',       order: 6 },
}

const SkeletonCard: React.FC = () => (
  <div className="rounded-lg border border-white/[0.1] bg-white/[0.06] p-3 animate-pulse">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-white/[0.08] flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3 bg-white/[0.08] rounded w-3/4" />
        <div className="h-3 bg-white/[0.08] rounded w-1/2" />
      </div>
      <div className="w-16 h-8 rounded-lg bg-white/[0.08]" />
    </div>
  </div>
)

interface ProductPanelProps {
  areaHa: number
  onAddToCart: (products: string[], totalPrice: number) => void
  className?: string
  /** Footer button label (e.g. "Encomendar" in the in-map order flow). */
  ctaLabel?: string
  /** Show a busy label + disable the footer button while an order is placing. */
  submitting?: boolean
  /** Disable the footer button without the busy label (e.g. missing name). */
  disabled?: boolean
}

export const ProductPanel: React.FC<ProductPanelProps> = ({
  areaHa,
  onAddToCart,
  className,
  ctaLabel = 'Adicionar ao Carrinho',
  submitting = false,
  disabled = false,
}) => {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    productsService.getProducts().then(({ products: fetched, error: err }) => {
      if (cancelled) return
      if (err) {
        setError('Não foi possível carregar os produtos. Verifique a ligação.')
      } else {
        setProducts(fetched)
      }
      setLoading(false)
    })

    return () => { cancelled = true }
  }, [])

  // Exclude bundled products from picker (auto-included, not selectable).
  const paidProducts = products.filter(p => p.pricing_strategy !== 'bundled')
  const bundledProducts = products.filter(p => p.pricing_strategy === 'bundled')

  const groups = React.useMemo(() => {
    const grouped: Record<string, Product[]> = {}
    for (const p of paidProducts) {
      if (!grouped[p.category]) grouped[p.category] = []
      grouped[p.category].push(p)
    }
    return Object.entries(grouped).sort(([a], [b]) => {
      const orderA = GROUP_META[a]?.order ?? 99
      const orderB = GROUP_META[b]?.order ?? 99
      return orderA - orderB
    })
  }, [paidProducts])

  const toggleProduct = (code: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(code)) {
        next.delete(code)
      } else {
        next.add(code)
      }
      return next
    })
  }

  const calculateTotal = () => {
    let total = 0
    for (const code of selected) {
      const product = paidProducts.find(p => p.code === code)
      if (product) total += calculateProductPrice(product, areaHa)
    }
    return total
  }

  const handleAddToCart = () => {
    const codes = Array.from(selected)
    if (codes.length > 0) {
      // Selection is intentionally kept: in the order flow the parent navigates
      // away on success, and keeping it means a failed order does not lose the
      // user's picks.
      onAddToCart(codes, calculateTotal())
    }
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn('space-y-4', className)}>
        {/* Header */}
        <div>
          <h3 className="font-medium text-sm">Dados do Terreno</h3>
          <p className="text-xs text-muted-foreground">
            Área: {areaHa.toFixed(2)} ha
          </p>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="space-y-2">
            {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Product groups */}
        {!loading && !error && groups.map(([category, categoryProducts]) => {
          const groupMeta = GROUP_META[category]
          const groupLabel = groupMeta ? groupMeta.label : category

          return (
            <div key={category} className="space-y-2">
              {/* Group heading */}
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {groupLabel}
                </h4>
                <span className="text-xs text-muted-foreground">
                  ({categoryProducts.length})
                </span>
              </div>

              {/* Products in group */}
              {categoryProducts.map(product => {
                const meta = PRODUCT_META[product.code]
                const Icon = meta?.icon ?? Info
                const isSelected = selected.has(product.code)
                const price = calculateProductPrice(product, areaHa)
                const atMin = isAtMinPrice(product, areaHa)

                return (
                  <div
                    key={product.code}
                    className={cn(
                      'rounded-lg border transition-all',
                      isSelected
                        ? 'border-accent bg-accent/5'
                        : 'border-white/[0.1] bg-white/[0.06]'
                    )}
                  >
                    <div className="flex items-center gap-3 p-3">
                      {/* Icon */}
                      <div
                        className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                          isSelected
                            ? 'bg-accent/20 text-accent'
                            : 'bg-white/[0.06] text-muted-foreground'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>

                      {/* Name + badges */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-sm truncate">{product.name}</span>
                          {meta?.whenToUse && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                                  onClick={e => e.stopPropagation()}
                                >
                                  <Info className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-xs">
                                {product.description && (
                                  <p className="text-xs text-muted-foreground mb-2">
                                    {product.description}
                                  </p>
                                )}
                                <p className="text-xs">
                                  <span className="font-medium">Quando usar: </span>
                                  {meta.whenToUse}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        {/* Resolution / format chips */}
                        <div className="flex items-center gap-1 mt-0.5">
                          {product.resolution && (
                            <span className="text-xs bg-white/[0.06] rounded px-1.5 py-0.5">
                              {product.resolution}
                            </span>
                          )}
                          {product.format && (
                            <span className="text-xs bg-white/[0.06] rounded px-1.5 py-0.5">
                              {product.format}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Toggle + price button */}
                      <button
                        type="button"
                        onClick={() => toggleProduct(product.code)}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-right flex-shrink-0',
                          'hover:border-accent/50',
                          isSelected
                            ? 'border-accent bg-accent text-white'
                            : 'border-white/[0.1] bg-white/[0.08]'
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                        <div>
                          <p className="text-sm font-medium leading-tight">
                            {price.toFixed(2)} €
                          </p>
                          {atMin && (
                            <p className={cn(
                              'text-xs leading-tight',
                              isSelected ? 'text-white/70' : 'text-muted-foreground'
                            )}>
                              mín.
                            </p>
                          )}
                          {!atMin && (
                            <p className={cn(
                              'text-xs leading-tight',
                              isSelected ? 'text-white/70' : 'text-muted-foreground'
                            )}>
                              {product.price_per_ha.toFixed(2)} €/ha
                            </p>
                          )}
                        </div>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}

        {/* Bundled OSM notice */}
        {!loading && !error && bundledProducts.length > 0 && (
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-xs text-muted-foreground">
            <span className="font-medium">Contexto OSM incluído gratuitamente</span>
            {' '}- estradas, edifícios, uso do solo, cursos de água e limites administrativos
            são adicionados automaticamente a qualquer encomenda.
          </div>
        )}

        {/* Add to cart sticky footer */}
        {selected.size > 0 && (
          <div className="sticky bottom-0 pt-3 pointer-events-none">
            <div className="bg-gradient-to-t from-basalt via-basalt/80 to-transparent absolute inset-0" />
            <div className="card-glass rounded-lg p-4 pointer-events-auto relative">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">
                  {selected.size} produto{selected.size !== 1 ? 's' : ''}
                </span>
                <span className="font-semibold text-lg">
                  {calculateTotal().toFixed(2)} €
                </span>
              </div>
              <Button
                type="button"
                className="w-full bg-accent hover:bg-accent/90"
                size="lg"
                disabled={submitting || disabled}
                onClick={handleAddToCart}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                {submitting ? 'A processar...' : ctaLabel}
              </Button>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}
